import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  Filter,
  X,
  Lock
} from 'lucide-react';
import { projectsAPI } from '../services/api';
import AddEventDialog from '../components/AddEventDialog';
import { useAuth } from '../contexts/AuthContext';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';

const Calendar = () => {
  const [projectsData, setProjectsData] = useState([]);
  const [filteredProjects, setFilteredProjects] = useState([]);
  const [currentDate, setCurrentDate] = useState(new Date(2025, 2, 1));
  const [selectedView, setSelectedView] = useState('month');
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState([]);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const { canCreate, } = useAuth();

  useEffect(() => {
    fetchProjects();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [projectsData, filterStatus, dateRange]);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getAll();
      setProjectsData(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Fout bij ophalen projecten:', error);
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...projectsData];

    if (filterStatus.length > 0) {
      filtered = filtered.filter(p => filterStatus.includes(p.status));
    }

    if (dateRange.start) {
      filtered = filtered.filter(p => new Date(p.startDate) >= new Date(dateRange.start));
    }
    if (dateRange.end) {
      filtered = filtered.filter(p => new Date(p.endDate) <= new Date(dateRange.end));
    }

    setFilteredProjects(filtered);
  };

  const toggleStatusFilter = (status) => {
    setFilterStatus(prev => 
      prev.includes(status) 
        ? prev.filter(s => s !== status)
        : [...prev, status]
    );
  };

  const clearFilters = () => {
    setFilterStatus([]);
    setDateRange({ start: '', end: '' });
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentDate);

  const getEventsForDay = (day) => {
    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    const dataToUse = filteredProjects.length > 0 || filterStatus.length > 0 || dateRange.start || dateRange.end 
      ? filteredProjects 
      : projectsData;
    
    return dataToUse.filter(project => {
      const startDate = new Date(project.startDate);
      const endDate = new Date(project.endDate);
      const checkDate = new Date(dateStr);
      return checkDate >= startDate && checkDate <= endDate;
    });
  };

  const previousMonth = () => {
    setCurrentDate(new Date(year, month - 1, 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(year, month + 1, 1));
  };

  const monthNames = ['Januari', 'Februari', 'Maart', 'April', 'Mei', 'Juni',
    'Juli', 'Augustus', 'September', 'Oktober', 'November', 'December'
  ];

  const dayNames = ['Zo', 'Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za'];

  const getStatusLabel = (status) => {
    const labels = {
      'quote': 'Offerte',
      'pending': 'In Afwachting',
      'confirmed': 'Bevestigd',
      'in-progress': 'In Uitvoering',
      'completed': 'Afgerond'
    };
    return labels[status] || status;
  };

  const dataToDisplay = filteredProjects.length > 0 || filterStatus.length > 0 || dateRange.start || dateRange.end 
    ? filteredProjects 
    : projectsData;

  const upcomingEvents = dataToDisplay
    .filter(p => new Date(p.startDate) >= new Date())
    .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
    .slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Agenda laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Agenda</h1>
          <p className="text-gray-500 mt-1">Plan en beheer uw projecten</p>
        </div>
        <div className="flex space-x-3">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" className="border-gray-300">
                <Filter className="w-4 h-4 mr-2" />
                Filter
                {(filterStatus.length > 0 || dateRange.start || dateRange.end) && (
                  <Badge className="ml-2 bg-blue-600 hover:bg-blue-600">
                    {filterStatus.length + (dateRange.start ? 1 : 0) + (dateRange.end ? 1 : 0)}
                  </Badge>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80">
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">Filters</h3>
                  {(filterStatus.length > 0 || dateRange.start || dateRange.end) && (
                    <Button variant="ghost" size="sm" onClick={clearFilters}>
                      <X className="w-4 h-4 mr-1" />
                      Wissen
                    </Button>
                  )}
                </div>

                <div className="space-y-2">
                  <Label className="text-sm font-medium">Status</Label>
                  <div className="flex flex-wrap gap-2">
                    {['pending', 'confirmed', 'in-progress', 'quote'].map(status => (
                      <Button
                        key={status}
                        size="sm"
                        variant={filterStatus.includes(status) ? 'default' : 'outline'}
                        onClick={() => toggleStatusFilter(status)}
                        className={filterStatus.includes(status) ? 'bg-blue-600' : ''}
                      >
                        {getStatusLabel(status)}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="startDate">Startdatum</Label>
                  <Input
                    id="startDate"
                    type="date"
                    value={dateRange.start}
                    onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="endDate">Einddatum</Label>
                  <Input
                    id="endDate"
                    type="date"
                    value={dateRange.end}
                    onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                  />
                </div>
              </div>
            </PopoverContent>
          </Popover>
          {canCreate() ? (
            <AddEventDialog onSuccess={fetchProjects} />
          ) : (
            <div className="flex items-center px-4 py-2 bg-gray-100 rounded-md text-gray-500">
              <Lock className="w-4 h-4 mr-2" />
              <span>Alleen lezen</span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <Card className="lg:col-span-3 shadow-lg border-0">
          <CardHeader className="border-b bg-gradient-to-r from-purple-50 to-blue-50">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center text-lg">
                <CalendarIcon className="w-5 h-5 mr-2 text-purple-600" />
                {monthNames[month]} {year}
              </CardTitle>
              <div className="flex items-center space-x-2">
                <Button variant="outline" size="icon" onClick={previousMonth}>
                  <ChevronLeft className="w-4 h-4" />
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setCurrentDate(new Date())}
                >
                  Vandaag
                </Button>
                <Button variant="outline" size="icon" onClick={nextMonth}>
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            <div className="grid grid-cols-7 gap-1">
              {dayNames.map(day => (
                <div key={day} className="p-2 text-center font-semibold text-gray-600 text-sm">
                  {day}
                </div>
              ))}
              
              {Array.from({ length: startingDayOfWeek }).map((_, i) => (
                <div key={`empty-${i}`} className="p-2 h-24 bg-gray-50 rounded"></div>
              ))}
              
              {Array.from({ length: daysInMonth }).map((_, i) => {
                const day = i + 1;
                const events = getEventsForDay(day);
                const isToday = new Date().getDate() === day && 
                               new Date().getMonth() === month && 
                               new Date().getFullYear() === year;
                
                return (
                  <div 
                    key={day} 
                    className={`p-2 h-24 border rounded transition-colors duration-200 ${
                      isToday ? 'bg-purple-50 border-purple-300' : 'bg-white hover:bg-gray-50'
                    }`}
                  >
                    <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-purple-600' : 'text-gray-700'}`}>
                      {day}
                    </div>
                    <div className="space-y-1 overflow-hidden">
                      {events.slice(0, 2).map((event, idx) => (
                        <div 
                          key={idx} 
                          className={`text-xs p-1 rounded truncate ${
                            event.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                            event.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                            event.status === 'quote' ? 'bg-gray-100 text-gray-700' :
                            'bg-yellow-100 text-yellow-700'
                          }`}
                          title={event.name}
                        >
                          {event.name}
                        </div>
                      ))}
                      {events.length > 2 && (
                        <div className="text-xs text-gray-500 font-medium">
                          +{events.length - 2} meer
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        <Card className="shadow-lg border-0">
          <CardHeader className="border-b bg-gradient-to-r from-green-50 to-blue-50">
            <CardTitle className="flex items-center text-lg">
              <CalendarIcon className="w-5 h-5 mr-2 text-green-600" />
              Aankomende Evenementen
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4">
            {upcomingEvents.length === 0 ? (
              <p className="text-gray-500 text-center py-4">Geen aankomende evenementen</p>
            ) : (
              <div className="space-y-3">
                {upcomingEvents.map((event, idx) => (
                  <div key={idx} className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors duration-200">
                    <h4 className="font-semibold text-gray-900 text-sm">{event.name}</h4>
                    <p className="text-xs text-gray-500 mt-1">{event.customer}</p>
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-gray-500">
                        {new Date(event.startDate).toLocaleDateString('nl-NL')}
                      </span>
                      <Badge className={`text-xs ${
                        event.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                        event.status === 'in-progress' ? 'bg-blue-100 text-blue-700' :
                        event.status === 'quote' ? 'bg-gray-100 text-gray-700' :
                        'bg-yellow-100 text-yellow-700'
                      }`}>
                        {getStatusLabel(event.status)}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Calendar;